import {
  AvatarGroup,
  Banner,
  BoundMultiSelectField,
  BoundNumberField,
  BoundSelectField,
  Button,
  Css,
  FormHeading,
  Palette,
  SuperDrawerContent,
  SuperDrawerHeader,
  Tag,
  useSuperDrawer,
} from "@homebound/beam";
import { ObjectConfig, required, useFormState } from "@homebound/form-state";
import { Observer } from "mobx-react";
import { useCallback } from "react";
import { UserFeatureFlagsDocument } from "src/contexts/FeatureFlags/FeatureFlagContext.generated";
import {
  FeatureFlagStatus,
  FeatureFlagTargetTarget,
  FeatureFlagType,
  InputMaybe,
  possibleTypes,
} from "src/generated/graphqlTypes";
import { internalUserAvatar, internalUserMenuLabel } from "src/utils/decorators/internalUserDecorators";
import {
  FeatureFlagDetailsFragment,
  FeatureFlagsDocument,
  useFeatureFlagsMetadataQuery,
  useSaveFeatureFlagMutation,
} from "./FeatureFlags.generated";

type FeatureFlagDrawerProps = { featureFlag?: FeatureFlagDetailsFragment; isReadonly: boolean };

export function FeatureFlagDrawer({ featureFlag, isReadonly }: FeatureFlagDrawerProps) {
  const isEdit = !!featureFlag;
  const { data } = useFeatureFlagsMetadataQuery({ variables: { all: isEdit } });
  const [saveFeatureFlag] = useSaveFeatureFlagMutation();
  const targets = featureFlag?.targets ?? [];
  const formState = useFormState({
    config,
    readOnly: isReadonly,
    init: {
      input: featureFlag,
      map: (featureFlag) => ({
        id: featureFlag.id,
        type: featureFlag.type.code,
        developmentTargets: idsOfTargetsOfType(targets, "Development", "id"),
        projectTargets: possibleTypes.Project.flatMap((type) =>
          idsOfTargetsOfType(targets, type as "StandaloneProject", "id"),
        ),
        userTargets: idsOfTargetsOfType(targets, "User", "userId").compact(),
        userGroupTargets: idsOfTargetsOfType(targets, "UserGroup", "id"),
        status: featureFlag.status.code ?? FeatureFlagStatus.Active,
        releaseVersion: featureFlag.releaseVersion,
      }),
    },
  });
  const { closeDrawer } = useSuperDrawer();

  const developments = data?.developments ?? [];
  const projects = data?.projects ?? [];
  const users = data?.internalUsers ?? [];
  const availableFeatureFlagTypes = (data?.availableFeatureFlagTypes ?? []).map((type) => ({
    label: type.name,
    value: type.code,
  }));

  const handleSaveFeatureFlag = useCallback(async () => {
    const { developmentTargets, projectTargets, userTargets, userGroupTargets, ...others } = formState.changedValue;
    // If any target changes, send them all, otherwise send none
    const allTargets = [developmentTargets, projectTargets, userTargets, userGroupTargets];
    const targetIds = allTargets.some((t) => t !== undefined)
      ? [
          ...(formState.developmentTargets.value ?? []),
          ...(formState.projectTargets.value ?? []),
          ...(formState.userTargets.value ?? []),
          ...(formState.userGroupTargets.value ?? []),
        ]
      : undefined;
    await saveFeatureFlag({
      variables: { input: { ...others, targetIds } },
      refetchQueries: [FeatureFlagsDocument, UserFeatureFlagsDocument],
    });
    closeDrawer();
  }, [closeDrawer, formState, saveFeatureFlag]);

  return (
    <Observer>
      {() => (
        <SuperDrawerContent>
          <SuperDrawerHeader title={`${isReadonly ? "View" : isEdit ? "Edit" : "Create"} Feature Flag`} />
          {isReadonly && (
            <div css={Css.mb3.$}>
              <Banner message="You do not have permission to edit feature flags" type="info" />
            </div>
          )}
          <div css={Css.df.fdc.gap2.h100.$}>
            <BoundSelectField
              label="Type"
              field={formState.type}
              options={availableFeatureFlagTypes}
              getOptionLabel={({ label }) => label}
              getOptionValue={({ value }) => value}
            />
            <BoundSelectField
              label="Status"
              field={formState.status}
              options={Object.values(FeatureFlagStatus).map((status) => ({ id: status, value: status }))}
              getOptionLabel={({ id }) => id}
              getOptionValue={({ value }) => value}
            />
            <BoundNumberField
              label="Release Version"
              field={formState.releaseVersion}
              helperText="Increment this number to prompt users in this flag group to refresh their browser."
            />
            {formState.status.value === FeatureFlagStatus.Active && (
              <>
                <FormHeading title="Targets" />
                <BoundMultiSelectField
                  label="Developments"
                  field={formState.developmentTargets}
                  options={developments}
                />
                <BoundMultiSelectField
                  label="Projects"
                  field={formState.projectTargets}
                  options={projects}
                  getOptionLabel={(p) => `${p.name} (${p.__typename})`}
                  getOptionMenuLabel={(p) => (
                    <div css={Css.w100.df.aic.jcsb.$}>
                      <div>{p.name}</div>
                      <Tag text={p.__typename} xss={Css.bgColor(projectTypeToColor(p.__typename)).white.$} />
                    </div>
                  )}
                />
                <BoundMultiSelectField
                  label="Users"
                  field={formState.userTargets}
                  options={users}
                  getOptionValue={({ user: { userId } }) => userId}
                  getOptionLabel={({ name }) => name}
                  fieldDecoration={internalUserAvatar}
                  getOptionMenuLabel={internalUserMenuLabel}
                />
                <BoundMultiSelectField
                  label="User Groups"
                  field={formState.userGroupTargets}
                  options={data?.userGroups ?? []}
                  getOptionMenuLabel={({ name, users }) => (
                    <div css={Css.w100.df.aic.jcsb.$}>
                      <div>{name}</div>
                      <AvatarGroup
                        avatars={users.map((u) => ({ src: u.internalUser?.avatar, name: u.internalUser?.name }))}
                      />
                    </div>
                  )}
                />
              </>
            )}
            {!isReadonly && (
              <div css={Css.df.jcfe.$}>
                <Button
                  label={isEdit ? "Save" : "Create"}
                  onClick={handleSaveFeatureFlag}
                  disabled={!formState.valid}
                />
              </div>
            )}
          </div>
        </SuperDrawerContent>
      )}
    </Observer>
  );
}

type FormValue = {
  id: InputMaybe<string>;
  type: InputMaybe<FeatureFlagType>;
  developmentTargets: InputMaybe<string[]>;
  projectTargets: InputMaybe<string[]>;
  userTargets: InputMaybe<string[]>;
  userGroupTargets: InputMaybe<string[]>;
  status: InputMaybe<FeatureFlagStatus>;
  releaseVersion: InputMaybe<number>;
};

const config: ObjectConfig<FormValue> = {
  id: { type: "value" },
  type: { type: "value", rules: [required] },
  developmentTargets: { type: "value" },
  projectTargets: { type: "value" },
  userTargets: { type: "value" },
  userGroupTargets: { type: "value" },
  status: { type: "value", rules: [required] },
  releaseVersion: { type: "value", rules: [required] },
};

type TargetType = FeatureFlagTargetTarget["__typename"];
type Target = FeatureFlagDetailsFragment["targets"][number]["target"];

function isTargetType<T extends TargetType>(type: T): (target: Target) => target is Extract<Target, { __typename: T }> {
  return function (target: Target): target is Extract<Target, { __typename: T }> {
    return target?.__typename === type;
  };
}

function targetsOfType<T extends TargetType>(
  targets: FeatureFlagDetailsFragment["targets"],
  type: T,
): Extract<Target, { __typename: T }>[] {
  return targets.map((t) => t.target).filter(isTargetType(type));
}

function idsOfTargetsOfType<T extends TargetType, F extends keyof Extract<Target, { __typename: T }>>(
  targets: FeatureFlagDetailsFragment["targets"],
  type: T,
  field: F,
) {
  return targetsOfType(targets, type).map((t) => t[field]);
}

function projectTypeToColor(typename: string | undefined): string {
  switch (typename) {
    case "Lot":
      return Palette.Green600;
    case "Structure":
      return Palette.Blue600;
    case "Unit":
      return Palette.Purple500;
    case "Community":
      return Palette.Orange600;
    case "Subdivision":
      return Palette.Gray700;
    case "Market":
      return Palette.Red500;
    case "Region":
      return Palette.Blue800;
    case "StandaloneProject":
    default:
      return Palette.Gray500;
  }
}
